import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HiOutlineSwitchVertical, HiOutlineX, HiOutlineCheckCircle } from "react-icons/hi";

import { routes } from "consts";
import { OpenInNewIcon } from "components/atoms";
import { EmptyState, AdminSearchInput } from "components/molecules";
import { formatDate, toMoneyFormat } from "utils";
import { PaziaLoader } from "components/atoms";
import { StudentCourseApi } from "api/student-courses/studentCoursesApi";
import { useStudentCourses } from "api/student-courses";
import { PaymentStatusBadge, paymentStateOf, paymentDotClass } from "./PaymentStatusBadge";

type CourseFilter = "all" | "paid" | "due";

const FILTER_OPTIONS: { key: CourseFilter; label: string }[] = [
  { key: "all", label: "Wszystkie" },
  { key: "paid", label: "Opłacone" },
  { key: "due", label: "Zaległości" },
];

const useCourseFilter = (data: StudentCourseApi[]) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<CourseFilter>("all");

  const sorted = useMemo(() => [...data].sort((a, b) => (a.registrationDate > b.registrationDate ? -1 : 1)), [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((c) => {
      const state = paymentStateOf(c.fullyPaid, c.paidAmount);
      if (filter === "paid" && state !== "paid") return false;
      if (filter === "due" && state === "paid") return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sorted, query, filter]);

  return { query, setQuery, filter, setFilter, filtered };
};

const FilterChips = ({ filter, onChange }: { filter: CourseFilter; onChange: (f: CourseFilter) => void }) => (
  <div className="flex flex-wrap gap-1.5">
    {FILTER_OPTIONS.map((opt) => (
      <button
        key={opt.key}
        type="button"
        onClick={() => onChange(opt.key)}
        className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
          filter === opt.key
            ? "bg-primary-500 text-white"
            : "bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

interface StudentCoursesProps {
  studentId: number;
}

export const StudentCourses = ({ studentId }: StudentCoursesProps) => {
  const { courseId } = useParams();
  const { courses, isLoading } = useStudentCourses(studentId);

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <>
      <Desktop data={courses} courseId={Number(courseId)} studentId={studentId} />
      <Mobile data={courses} courseId={Number(courseId)} studentId={studentId} />
    </>
  );
};

interface InternalProps {
  data: StudentCourseApi[];
  courseId: number;
  studentId: number;
}

const Desktop = ({ data, courseId, studentId }: InternalProps) => {
  const navigate = useNavigate();
  const { query, setQuery, filter, setFilter, filtered } = useCourseFilter(data);

  return (
    <div className="hidden bg-white md:block dark:bg-slate-800">
      <div className="flex max-h-[calc(100vh-12rem)] min-h-[400px] flex-col overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-700/50">
          <div className="mb-2.5 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Kursy uczestnika</h3>
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600 dark:bg-slate-600 dark:text-slate-200">
              {data.length}
            </span>
          </div>
          {data.length > 0 && (
            <>
              <AdminSearchInput onSearch={setQuery} className="mb-2" />
              <FilterChips filter={filter} onChange={setFilter} />
            </>
          )}
        </div>
        <div className="scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent flex-1 overflow-y-auto p-2">
          {data.length === 0 ? (
            <div className="flex h-full items-center justify-center p-8">
              <EmptyState
                title="Nic do wyświetlenia"
                description="Użytkownik nie zapisał się jeszcze na żaden kurs."
                centerDescription
              />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex h-full items-center justify-center p-8">
              <EmptyState title="Brak wyników" description="Żaden kurs nie pasuje do wybranego filtra." centerDescription />
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map(({ courseId: cId, name, registrationDate, fullyPaid, totalPrice, paidAmount }) => {
                const isActive = Number(courseId) === cId;
                const state = paymentStateOf(fullyPaid, paidAmount);

                return (
                  <div
                    key={cId}
                    className={`group relative cursor-pointer rounded-lg p-3 transition-colors duration-150 ${
                      isActive
                        ? "bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500"
                        : "border-l-4 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700/50"
                    }`}
                    onClick={() => navigate(routes.STUDENT_NESTED.COURSES(studentId, cId))}
                  >
                    <OpenInNewIcon href={routes.COURSES_MANAGE(cId)} />

                    <div className="pr-6">
                      <h4
                        className="mb-1 overflow-hidden text-sm font-semibold leading-tight text-slate-900 dark:text-slate-100"
                        style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                      >
                        {name}
                      </h4>
                      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                        {formatDate(registrationDate, false, false)}
                      </p>

                      <div className="flex items-center justify-between gap-2">
                        <PaymentStatusBadge state={state} />
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                          {toMoneyFormat(paidAmount)} / {toMoneyFormat(totalPrice, true)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Mobile = ({ data, courseId, studentId }: InternalProps) => {
  const [open, setOpen] = useState(false);

  if (data.length === 0) {
    return (
      <div className="col-span-12 flex min-h-[220px] items-center justify-center bg-white p-8 md:hidden dark:bg-slate-800">
        <EmptyState
          title="Nic do wyświetlenia"
          description="Użytkownik nie zapisał się jeszcze na żaden kurs."
          centerDescription
        />
      </div>
    );
  }

  const selected = data.find((c) => c.courseId === courseId) ?? data[0];
  const state = paymentStateOf(selected.fullyPaid, selected.paidAmount);

  return (
    <div className="border-b border-slate-200 bg-white p-3 md:hidden dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Wybrany kurs</span>
        <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
          {data.length} {data.length === 1 ? "kurs" : "kursów"}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left dark:border-slate-700 dark:bg-slate-900/40"
      >
        <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${paymentDotClass(state)}`} />
        <div className="min-w-0 flex-1">
          <div
            className="overflow-hidden text-sm font-semibold leading-tight text-slate-900 dark:text-slate-100"
            style={{ display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}
          >
            {selected.name}
          </div>
          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {formatDate(selected.registrationDate, false, false)}
          </div>
        </div>
        <span className="flex flex-shrink-0 items-center gap-1 text-xs font-bold text-primary-600 dark:text-primary-400">
          Zmień
          <HiOutlineSwitchVertical size={16} />
        </span>
      </button>

      {open && (
        <CoursePickerSheet
          data={data}
          courseId={courseId}
          studentId={studentId}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

interface CoursePickerSheetProps {
  data: StudentCourseApi[];
  courseId: number;
  studentId: number;
  onClose: () => void;
}

const CoursePickerSheet = ({ data, courseId, studentId, onClose }: CoursePickerSheetProps) => {
  const navigate = useNavigate();
  const { query, setQuery, filter, setFilter, filtered } = useCourseFilter(data);

  const handleSelect = (cId: number) => {
    navigate(routes.STUDENT_NESTED.COURSES(studentId, cId));
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 top-16 z-50 flex flex-col rounded-t-2xl bg-white dark:bg-slate-800">
        <div className="mx-auto mb-1 mt-2 h-1 w-10 flex-shrink-0 rounded-full bg-slate-300 dark:bg-slate-600" />
        <div className="flex-shrink-0 px-4 pb-3 pt-1">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Wybierz kurs ({data.length})</h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
            >
              <HiOutlineX size={20} />
            </button>
          </div>
          <AdminSearchInput onSearch={setQuery} className="mb-2" />
          <FilterChips filter={filter} onChange={setFilter} />
        </div>
        <div className="scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent flex-1 overflow-y-auto px-2 pb-4">
          {filtered.length === 0 ? (
            <div className="flex h-full items-center justify-center p-8">
              <EmptyState title="Brak wyników" description="Żaden kurs nie pasuje do wybranego filtra." centerDescription />
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map(({ courseId: cId, name, registrationDate, fullyPaid, totalPrice, paidAmount }) => {
                const isActive = Number(courseId) === cId;
                const state = paymentStateOf(fullyPaid, paidAmount);

                return (
                  <button
                    type="button"
                    key={cId}
                    onClick={() => handleSelect(cId)}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${
                      isActive
                        ? "border-primary-300 bg-primary-50 dark:border-primary-600 dark:bg-primary-900/20"
                        : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
                    }`}
                  >
                    <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${paymentDotClass(state)}`} />
                    <div className="min-w-0 flex-1">
                      <div
                        className="overflow-hidden text-sm font-semibold leading-tight text-slate-900 dark:text-slate-100"
                        style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                      >
                        {name}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDate(registrationDate, false, false)}
                        </span>
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                          {toMoneyFormat(paidAmount)} / {toMoneyFormat(totalPrice, true)}
                        </span>
                      </div>
                    </div>
                    {isActive && <HiOutlineCheckCircle size={20} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
